/**
 * Mobile bottom sheet -- a portal-backed modal that slides up from
 * the bottom edge with a translucent backdrop.
 *
 * The mobile dashboard packs CenterBook + EventFeed into a compact
 * strip of chips along the bottom of the screen.  Tapping a chip
 * opens that section in a BottomSheet so the Pixi canvas (the
 * building exterior with pod windows) gets the rest of the viewport
 * instead of fighting a 390 px sidebar for vertical space.
 *
 * Behaviour:
 *   * Backdrop click → ``onClose``.
 *   * Escape key → ``onClose`` (web kbd users still get a dismiss).
 *   * Body scroll is locked while the sheet is open so swiping
 *     inside the sheet doesn't double-scroll the building behind.
 *   * Content is rendered into ``document.body`` via portal so the
 *     parent stacking context can't clip the sheet.
 *
 * The sheet caps its height at ``--bottom-sheet-max`` (default
 * ``85vh``); content scrolls inside.  Drag-to-dismiss is intentionally
 * NOT implemented -- the standard close button + backdrop tap covers
 * 100 % of dismiss cases without the iOS-specific gesture complexity.
 */
import {
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

interface BottomSheetProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Right-side header content (e.g. badge or live count). */
  rightSlot?: ReactNode;
}

export function BottomSheet({
  open,
  title,
  onClose,
  children,
  rightSlot,
}: BottomSheetProps): ReactNode {
  const sheetRef = useRef<HTMLDivElement | null>(null);

  // Escape-to-close.  Bind only while open so a stray escape on the
  // main view doesn't fire anything unexpected.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Body scroll lock so swiping a long list inside the sheet doesn't
  // also scroll the page underneath.  Restore the prior overflow on
  // close so we don't trap the body when other modals share the page.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const onBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only dismiss on backdrop clicks, not on clicks inside the panel.
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(1, 4, 9, 0.65)',
        zIndex: 9000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        // Smooth fade-in to keep the canvas from snapping to dark.
        animation: 'bottomSheetFadeIn 140ms ease-out',
      }}
    >
      <div
        ref={sheetRef}
        // Stop click propagation so taps inside the sheet don't
        // bubble to the backdrop and dismiss the popup.
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0d1117',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderTop: '1px solid #21262d',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -8px 28px rgba(0, 0, 0, 0.55)',
          animation: 'bottomSheetSlideUp 180ms ease-out',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Drag-affordance grabber -- purely cosmetic, signals "this
           pulls down" even without a working swipe gesture. */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
          <span
            aria-hidden
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: '#30363d',
            }}
          />
        </div>
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '6px 16px 10px',
            borderBottom: '1px solid #21262d',
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#e6edf3',
              letterSpacing: 0.3,
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {rightSlot}
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрыть"
              style={{
                all: 'unset',
                cursor: 'pointer',
                width: 32,
                height: 32,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                color: '#8b949e',
                background: '#161b22',
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        </header>
        <div
          style={{
            overflowY: 'auto',
            padding: '12px 14px 18px',
            // The actual scrollable area -- give it some breathing
            // room at the bottom for the iOS home-bar gesture.
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
